import React, { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import './productTour.css';

import { useAuth } from '../../context/AuthContext';
import { useActiveProject } from '../../context/ActiveProjectContext';
import { useCompanyProjects } from '../../context/CompanyProjectsContext';
import { useMenuAccess } from '../../context/MenuAccessContext';
import { useSidebar } from '../../context/SidebarContext';
import { useLanguage } from '../../context/LanguageContext';
import { useProgressiveNavContext } from '../../hooks/useProgressiveNavContext';
import { useIsMobileLayout } from '../../hooks/useMediaQuery';
import { useProductTour } from '../../context/ProductTourContext';
import {
  delay,
  resolveActiveSteps,
  resolveTourId,
  runStepBeforeShow,
} from './tourUtils';
import { shouldForceProductTour } from './productTourStorage';
import {
  startSpotlightBorderTracking,
  stopSpotlightBorderTracking,
} from './tourSpotlightBorder';

function isTypingTarget(target) {
  if (!target) return false;
  const tag = target.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    (typeof target.isContentEditable === 'boolean' && target.isContentEditable)
  );
}

function computeHideAppFooter(pathname) {
  // Mirror the rules in App.js: cover both the new /drilling/* paths and the
  // legacy /operations/drilling/* paths while the back-compat redirects are live.
  const crewReportFormPath =
    (pathname.startsWith('/drilling/crew-report/') &&
      !pathname.startsWith('/drilling/crew-report-field')) ||
    (pathname.startsWith('/operations/drilling/crew-report/') &&
      !pathname.startsWith('/operations/drilling/crew-report-field'));
  return !(
    pathname.startsWith('/platform') ||
    pathname.includes('/operations/fuel-control/supervisor') ||
    pathname.includes('/drilling/progress-map') ||
    pathname.includes('/drilling/crew-report-field') ||
    pathname.includes('/operations/field-operations') ||
    pathname.includes('/operations/drilling/crew-report-field') ||
    crewReportFormPath
  );
}

const ProductTourHost = () => {
  const { user } = useAuth();
  const { activeProject } = useActiveProject();
  const { hasProjects, projectsLoaded } = useCompanyProjects();
  const { menusLoaded } = useMenuAccess();
  const {
    collapsed,
    sidebarHidden,
    sidebarMode,
    expandSidebar,
    revealSidebar,
    minimizeSidebar,
  } = useSidebar();
  const { isDemo, isDemoProjectBootstrap } = useProgressiveNavContext();
  const { t } = useLanguage();
  const isMobile = useIsMobileLayout();
  const location = useLocation();

  const {
    buildTourContext,
    startTour,
    stopTour,
    markTourCompleted,
    setIsTourActive,
    pendingTourId,
    setPendingTourId,
  } = useProductTour();

  const driverRef = useRef(null);
  const activeStepsRef = useRef([]);
  const activeTourIdRef = useRef(null);
  const autoStartAttemptedRef = useRef(false);
  const keyboardHandlerRef = useRef(null);
  const tourInProgressRef = useRef(false);
  const suppressCompleteOnDestroyRef = useRef(false);
  const runTourRef = useRef(null);
  const sidebarStateRef = useRef({ collapsed, sidebarHidden, sidebarMode });
  const layoutRef = useRef({
    isMobile,
    isDemo,
    isDemoProjectBootstrap,
    hasProjects,
    projectsLoaded,
    activeProject,
    menusLoaded,
    user,
    pathname: location.pathname,
  });

  useEffect(() => {
    sidebarStateRef.current = { collapsed, sidebarHidden, sidebarMode };
  }, [collapsed, sidebarHidden, sidebarMode]);

  useEffect(() => {
    layoutRef.current = {
      isMobile,
      isDemo,
      isDemoProjectBootstrap,
      hasProjects,
      projectsLoaded,
      activeProject,
      menusLoaded,
      user,
      pathname: location.pathname,
    };
  }, [
    isMobile,
    isDemo,
    isDemoProjectBootstrap,
    hasProjects,
    projectsLoaded,
    activeProject,
    menusLoaded,
    user,
    location.pathname,
  ]);

  const sidebarActions = useCallback(
    () => ({
      expandSidebar,
      revealSidebar,
      minimizeSidebar,
    }),
    [expandSidebar, revealSidebar, minimizeSidebar],
  );

  const cleanupKeyboardHandler = useCallback(() => {
    if (keyboardHandlerRef.current) {
      window.removeEventListener('keydown', keyboardHandlerRef.current);
      keyboardHandlerRef.current = null;
    }
  }, []);

  const destroyDriver = useCallback(({ markComplete = true } = {}) => {
    cleanupKeyboardHandler();
    stopSpotlightBorderTracking();
    if (driverRef.current?.isActive?.()) {
      suppressCompleteOnDestroyRef.current = !markComplete;
      driverRef.current.destroy();
    }
    driverRef.current = null;
    tourInProgressRef.current = false;
    setIsTourActive(false);
  }, [cleanupKeyboardHandler, setIsTourActive]);

  const buildLiveTourContext = useCallback(() => {
    const layout = layoutRef.current;
    const sidebar = sidebarStateRef.current;
    return buildTourContext({
      isMobile: layout.isMobile,
      isDemo: layout.isDemo,
      isDemoProjectBootstrap: layout.isDemoProjectBootstrap,
      hasProjects: layout.hasProjects,
      projectsLoaded: layout.projectsLoaded,
      activeProject: layout.activeProject,
      collapsed: sidebar.collapsed,
      sidebarHidden: sidebar.sidebarHidden,
      sidebarMode: sidebar.sidebarMode,
      menusLoaded: layout.menusLoaded,
      user: layout.user,
      isOnAppHome: layout.pathname === '/app',
      showAppFooter: computeHideAppFooter(layout.pathname),
    });
  }, [buildTourContext]);

  const runTour = useCallback(
    async (tourId, { force = false } = {}) => {
      const layout = layoutRef.current;
      if (!layout.user || !layout.menusLoaded) return false;

      if (tourInProgressRef.current && driverRef.current?.isActive?.()) {
        return false;
      }

      const ctx = buildLiveTourContext();
      const resolvedId = tourId || resolveTourId(ctx);
      const steps = resolveActiveSteps(resolvedId, ctx);

      if (steps.length === 0) return false;

      // Clear pending before sidebar/layout updates so the effect does not re-run mid-tour.
      setPendingTourId(null);
      destroyDriver({ markComplete: false });
      activeStepsRef.current = steps;
      activeTourIdRef.current = resolvedId;
      tourInProgressRef.current = true;

      const driverSteps = steps.map((step, index) => ({
        element: step.target,
        popover: {
          title: t(step.titleKey, step.titleKey),
          description: t(step.bodyKey, step.bodyKey),
          side: step.placement || 'bottom',
          align: 'start',
          popoverClass: 'archon-product-tour-popover',
          showProgress: true,
          progressText: t('tour.actions.progress', '{{current}} of {{total}}')
            .replace('{{current}}', String(index + 1))
            .replace('{{total}}', String(steps.length)),
          nextBtnText: t('tour.actions.next', 'Next'),
          prevBtnText: t('tour.actions.prev', 'Back'),
          doneBtnText: t('tour.actions.finish', 'Got it'),
        },
        onHighlightStarted: async () => {
          const current = activeStepsRef.current[index];
          if (!current) return;
          const liveCtx = buildLiveTourContext();
          await runStepBeforeShow(current, liveCtx, sidebarActions());
          await delay(200);
          driverRef.current?.refresh?.();
        },
      }));

      const driverObj = driver({
        animate: true,
        overlayColor: 'var(--archon-navy-deep)',
        overlayOpacity: 0.78,
        stagePadding: 8,
        stageRadius: 8,
        allowClose: true,
        overlayClickBehavior: 'close',
        showProgress: true,
        popoverClass: 'archon-product-tour-popover',
        nextBtnText: t('tour.actions.next', 'Next'),
        prevBtnText: t('tour.actions.prev', 'Back'),
        doneBtnText: t('tour.actions.finish', 'Got it'),
        steps: driverSteps,
        onDestroyed: () => {
          cleanupKeyboardHandler();
          stopSpotlightBorderTracking();
          if (activeTourIdRef.current && !suppressCompleteOnDestroyRef.current) {
            markTourCompleted(activeTourIdRef.current);
          }
          suppressCompleteOnDestroyRef.current = false;
          activeTourIdRef.current = null;
          activeStepsRef.current = [];
          driverRef.current = null;
          tourInProgressRef.current = false;
          setIsTourActive(false);
          setPendingTourId(null);
        },
      });

      driverRef.current = driverObj;
      setIsTourActive(true);
      startSpotlightBorderTracking();

      keyboardHandlerRef.current = (event) => {
        if (!driverRef.current?.isActive?.()) return;
        if (isTypingTarget(event.target)) return;

        if (event.key === 'Escape') {
          event.preventDefault();
          driverRef.current.destroy();
          return;
        }

        if (event.key === 'ArrowRight' || event.key === 'Enter') {
          event.preventDefault();
          driverRef.current.moveNext();
          return;
        }

        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          driverRef.current.movePrevious();
        }
      };
      window.addEventListener('keydown', keyboardHandlerRef.current);

      driverObj.drive();
      return true;
    },
    [
      buildLiveTourContext,
      destroyDriver,
      sidebarActions,
      t,
      markTourCompleted,
      setIsTourActive,
      setPendingTourId,
      cleanupKeyboardHandler,
    ],
  );

  runTourRef.current = runTour;

  useEffect(() => {
    if (!pendingTourId) return undefined;
    const { id: tourId, force } = pendingTourId;
    let cancelled = false;

    (async () => {
      await delay(400);
      if (cancelled) return;
      await runTourRef.current?.(tourId, { force });
    })();

    return () => {
      cancelled = true;
    };
  }, [pendingTourId]);

  useEffect(() => {
    if (!user || !menusLoaded) return undefined;
    if (location.pathname !== '/app') return undefined;
    if (autoStartAttemptedRef.current) return undefined;
    if (tourInProgressRef.current) return undefined;

    const ctx = buildLiveTourContext();
    const tourId = resolveTourId(ctx);
    const force = shouldForceProductTour();

    autoStartAttemptedRef.current = true;

    startTour(tourId, { force });
  }, [user, menusLoaded, location.pathname, buildLiveTourContext, startTour]);

  useEffect(() => () => {
    destroyDriver();
    stopTour();
  }, [destroyDriver, stopTour]);

  return null;
};

export default ProductTourHost;
